import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Flex } from '@televet/kibble-ui/chakra';
import { Button, TextInput } from '@televet/kibble-ui';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { EventName, LocalStorageKey } from '@/lib/constants';
import { useLoginTracking, useStepViewed } from '@/hooks/useLoginTracking';
import { LoginCard } from './-components/LoginCard';
import { LoginLayout } from './-components/LoginLayout';
import { LoginProgress } from './-components/LoginProgress';
import { InformLoginErrorCard } from '../tokenlogin/-components/InformLoginErrorCard';
import { useCompleteInformLogin } from './-hooks/useCompleteInformLogin';
import { useInformLoginIdentify } from './-hooks/useInformLoginIdentify';

export const Route = createFileRoute('/login/complete')({
  component: InformLoginCompletePage,
});

const getStoredEmail = (): string => window.localStorage.getItem(LocalStorageKey.LOGIN_EMAIL) ?? '';

// Clinic id is baked into the link for multi-clinic users
const getClinicIdFromUrl = (): string | undefined =>
  new URLSearchParams(window.location.search).get('informClinicId') ?? undefined;

function InformLoginCompletePage() {
  const hasRunRef = useRef(false);
  const [email, setEmail] = useState(getStoredEmail);
  const [needsEmail, setNeedsEmail] = useState(() => !getStoredEmail());
  const { track } = useLoginTracking();
  const { setVerifiedEmail, identifyResolvedClinic } = useInformLoginIdentify();
  const { loginState, migrationState, isMigrating, completeLogin, setError } =
    useCompleteInformLogin({
      authMethod: 'MAGIC_LINK',
      informClinicId: getClinicIdFromUrl(),
      // Reads the clinic from the server's response rather than the URL, so links sent
      // before this shipped — which carry no informClinicId — are covered too.
      onInformClinicResolved: identifyResolvedClinic,
    });

  // Fire once when the migration loading screen appears within it.
  useStepViewed(EventName.INFORM_LOGIN_MIGRATION_LOADING_VIEWED, isMigrating, {
    surface: 'login',
  });

  const runSignIn = useCallback(
    async (emailAddress: string) => {
      track(EventName.INFORM_LOGIN_MAGIC_LINK_USED, { surface: 'login' });

      // Guard against multiple runs, if the effect re-runs while resolving needsEmail.
      if (!isSignInWithEmailLink(auth, window.location.href)) {
        setError({ kind: 'error', status: 'INVALID_TOKEN' });
        return;
      }

      try {
        const credential = await signInWithEmailLink(auth, emailAddress, window.location.href);

        // Captured before the removal below, since completion reads it back for identify.
        setVerifiedEmail(credential.user.email ?? emailAddress);

        // Remove the stored email; it was only needed for the sign-in step
        window.localStorage.removeItem(LocalStorageKey.LOGIN_EMAIL);

        const idToken = await credential.user.getIdToken();
        completeLogin({ idToken, method: 'MAGIC_LINK' });
      } catch {
        setError({ kind: 'error', status: 'INVALID_TOKEN' });
      }
    },
    [completeLogin, setError, track, setVerifiedEmail]
  );

  // Run the email-link sign-in once an email is available; ref guard prevents a
  // second attempt when needsEmail resolving re-runs the effect.
  useEffect(() => {
    if (hasRunRef.current || needsEmail || !email) return;

    hasRunRef.current = true;
    void runSignIn(email);
  }, [email, needsEmail, runSignIn]);

  if (loginState.kind === 'error') {
    return (
      <LoginLayout>
        <InformLoginErrorCard status={loginState.status} />
      </LoginLayout>
    );
  }

  if (migrationState.kind === 'error') {
    return (
      <LoginLayout>
        <InformLoginErrorCard status={migrationState.status} />
      </LoginLayout>
    );
  }

  if (needsEmail) {
    return (
      <LoginLayout>
        <LoginCard
          title="Confirm your email"
          subtitle="Enter the email address this link was sent to."
        >
          <Flex
            as="form"
            {...{ noValidate: true }}
            direction="column"
            align="stretch"
            gap={2}
            onSubmit={(e) => {
              e.preventDefault();
              setNeedsEmail(false);
            }}
          >
            <TextInput
              label="Email"
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" fullWidth mt={4}>
              Continue
            </Button>
          </Flex>
        </LoginCard>
      </LoginLayout>
    );
  }

  return (
    <LoginLayout>
      <LoginCard title="Log In" hideSupportText>
        <LoginProgress label={isMigrating ? 'Setting you up…' : 'Signing you in…'} />
      </LoginCard>
    </LoginLayout>
  );
}
